import { CellValueChangedEvent } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportPartTable } from 'bom/components';
import { ProblemNumber } from 'bom/elements';
import { ManufacturerValidationStatus, SolveProblemsStep } from 'bom/enums';
import { useBomImportActions, useGetBomImportNotValidParts } from 'bom/hooks';
import { useBomPartsImportContext } from 'bom/states';
import { IImportNotValidPartsPayload, IImportPart } from 'bom/types';
import { mapUpsertImportPartsData } from 'bom/utils';
import { Notification } from 'core/components';
import { NotificationCard, Title } from 'core/elements';
import { NotificationCardType, NotificationPosition } from 'core/enums';
import { AgGridClientTableProvider, useInnerStepsContext } from 'core/states';
import { GetManyResponse } from 'core/types';

import { getColumnDefs } from './columns';

interface IProps {
  isValid: boolean;
}

const PartsWithMultipleManufacturers = ({ isValid }: IProps) => {
  const { t } = useTranslation();

  const { activeStep, goNext } = useInnerStepsContext();
  const { partImportId, problems, setProblems, isAlternateVersionEnabled } =
    useBomPartsImportContext();

  const allValid = useMemo(
    () => isValid || problems[SolveProblemsStep.MULTIPLE_MANUFACTURERS]?.unsolved === 0,
    [isValid, problems]
  );

  const payload: IImportNotValidPartsPayload = useMemo(
    () => ({
      filters: {
        importId: partImportId,
        manufacturerValidationStatus: ManufacturerValidationStatus.MULTIPLE_MANUFACTURERS,
        omit: '"0"',
      },
    }),
    [partImportId]
  );

  const enabled = useMemo(
    () => !isValid && activeStep?.type === SolveProblemsStep.MULTIPLE_MANUFACTURERS,
    [isValid, activeStep]
  );

  const { records, isLoading } = useGetBomImportNotValidParts(
    payload,
    enabled,
    (data: GetManyResponse<IImportPart>) => {
      const records = data.records || [];

      setProblems((prev) => ({
        ...prev,
        [SolveProblemsStep.MULTIPLE_MANUFACTURERS]: {
          solved: 0,
          unsolved: records.length,
        },
      }));
    }
  );

  const { upsertImportParts } = useBomImportActions();

  useEffect(() => {
    if (!allValid) {
      return;
    }

    setTimeout(() => {
      goNext();
    }, 1000);
  }, [allValid, goNext]);

  const mappedRecords = useMemo(
    () =>
      records.map((record) => ({
        id: record?.id,
        description: record?.description,
        number: record?.number,
        possibleManufacturers: record?.manufacturerOptions,
        manufacturer: record?.manufacturerId
          ? { name: record?.manufacturer, id: record?.manufacturerId }
          : null,
        omit: false,
        doNotAssignManufacturer: false,
      })),
    [records]
  );

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressMovable: true,
      enableRowGroup: false,
      autoHeight: true,
      maxWidth: 300,
    }),
    []
  );

  const columnDefs = useMemo(
    () => getColumnDefs(isAlternateVersionEnabled),
    [isAlternateVersionEnabled]
  );

  const onCellValueChanged = useCallback(
    async (_, { colDef: { field }, data, newValue, api }: CellValueChangedEvent) => {
      api.setGridOption('loading', true);

      const { success } = await upsertImportParts(
        mapUpsertImportPartsData([data], field, newValue)
      );

      if (!success) {
        api.setGridOption('loading', false);

        return;
      }

      setProblems((prev) => ({
        ...prev,
        [SolveProblemsStep.MULTIPLE_MANUFACTURERS]: {
          solved: prev[SolveProblemsStep.MULTIPLE_MANUFACTURERS]?.solved + 1,
          unsolved: prev[SolveProblemsStep.MULTIPLE_MANUFACTURERS]?.unsolved - 1,
        },
      }));

      api.setGridOption('loading', false);

      return { removedRow: data, options: { redrawRows: false } };
    },
    [upsertImportParts, setProblems]
  );

  return isLoading || allValid ? (
    <>
      <Title text={t('titleMultipleManufacturers')} />

      <NotificationCard
        title={isLoading ? t('titleStatus') : t('titleSuccess')}
        message={isLoading ? t('messagePartsValidating') : t('messagePartsValidated')}
        type={isLoading ? NotificationCardType.LOADING : NotificationCardType.SUCCESS}
      />
    </>
  ) : (
    <>
      <Title
        text={t('titleMultipleManufacturers')}
        subtitle={
          <>
            <ProblemNumber
              number={
                problems[SolveProblemsStep.MULTIPLE_MANUFACTURERS]?.unsolved || mappedRecords.length
              }
            />
            <ProblemNumber
              number={problems[SolveProblemsStep.MULTIPLE_MANUFACTURERS]?.solved || 0}
              solved
            />
          </>
        }
      />

      <Notification position={NotificationPosition.DRAWER} />

      <AgGridClientTableProvider
        tableId="partsWithMultipleManufacturers"
        entityType="Part"
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        data={mappedRecords}
        isDataLoading={isLoading}
        onCellValueChanged={onCellValueChanged}
        loadingPercentage={isLoading ? 0 : 100}
        dataCurrentPage={isLoading ? undefined : 1}
        sideBar={{}}
        autoSizeStrategy={{
          type: 'fitCellContents',
        }}
        headerCheckbox={!isLoading}
      >
        <ImportPartTable
          filters={payload.filters}
          stepType={SolveProblemsStep.MULTIPLE_MANUFACTURERS}
        />
      </AgGridClientTableProvider>
    </>
  );
};

export default PartsWithMultipleManufacturers;
